import type { OfficeBillingMap, OfficeBillingTotals } from "../Services/Billing";

export type AccountingSorting = { field: string; order: "asc" | "desc" };

type BillingCategory = keyof OfficeBillingTotals;
type AmountKind = "invoiced" | "paid";

// Table columns whose displayed value is computed on the client from the Billing
// aggregates (merged into each row after the server responds). The `office`
// endpoint knows nothing about them, so they can only be sorted client-side.
const AMOUNT_FIELD_MAP: Record<string, { cat: BillingCategory; kind: AmountKind }> =
  {
    initiationFeesInvoiced: { cat: "initiation", kind: "invoiced" },
    initiationFeesPaid: { cat: "initiation", kind: "paid" },
    annualDuesInvoiced: { cat: "annualDues", kind: "invoiced" },
    annualDuesPaid: { cat: "annualDues", kind: "paid" },
    assessmentDuesInvoiced: { cat: "assessmentDues", kind: "invoiced" },
    assessmentDuesPaid: { cat: "assessmentDues", kind: "paid" },
    specialFundInvoiced: { cat: "specialFund", kind: "invoiced" },
    specialFundPaid: { cat: "specialFund", kind: "paid" },
    annualMeetingRegInvoiced: { cat: "annualMeeting", kind: "invoiced" },
    annualMeetingRegPaid: { cat: "annualMeeting", kind: "paid" },
    lateFeesInvoiced: { cat: "lateFees", kind: "invoiced" },
    lateFeesPaid: { cat: "lateFees", kind: "paid" },
  };

export const ACCOUNTING_CLIENT_SORT_FIELDS = new Set<string>([
  "firm",
  "invoiceDate",
  "paymentStatus",
  "balance",
  ...Object.keys(AMOUNT_FIELD_MAP),
]);

/** Firm column label. The office `firmName` is filled later from the long name. */
export function officeFirmShortLabel(office: {
  firmShortName?: unknown;
  firmName?: unknown;
  name?: unknown;
}): string {
  const shortName = String(office.firmShortName ?? "").trim();
  if (shortName) {
    return shortName;
  }
  return String(office.firmName ?? office.name ?? "").trim();
}

export function isAccountingClientSortField(field: string): boolean {
  return ACCOUNTING_CLIENT_SORT_FIELDS.has(field);
}

export function matchesAccountingPaymentStatusFilter(
  officeId: string,
  paymentStatusFilter: string,
  billingMap: OfficeBillingMap,
): boolean {
  if (!paymentStatusFilter || paymentStatusFilter === "all") {
    return true;
  }

  const status = String(billingMap[officeId]?.status ?? "")
    .trim()
    .toLowerCase();

  if (paymentStatusFilter === "paid") {
    return status === "paid";
  }
  if (paymentStatusFilter === "partial") {
    return status === "partial";
  }
  if (paymentStatusFilter === "unpaid") {
    return (
      status === "unpaid" ||
      status === "" ||
      status === "cancelled"
    );
  }

  return true;
}

export function filterAccountingRawDataByPaymentStatus<
  T extends { id?: unknown },
>(data: T[], paymentStatusFilter: string, billingMap: OfficeBillingMap): T[] {
  if (!paymentStatusFilter || paymentStatusFilter === "all") {
    return data;
  }
  return data.filter((row) =>
    matchesAccountingPaymentStatusFilter(
      String(row.id ?? ""),
      paymentStatusFilter,
      billingMap,
    ),
  );
}

function sumInvoiced(totals: OfficeBillingTotals): number {
  return (
    totals.initiation.invoiced +
    totals.annualDues.invoiced +
    totals.assessmentDues.invoiced +
    totals.membershipBalance.invoiced +
    totals.specialFund.invoiced +
    totals.annualMeeting.invoiced +
    totals.lateFees.invoiced
  );
}

function sumPaid(totals: OfficeBillingTotals): number {
  return (
    totals.initiation.paid +
    totals.annualDues.paid +
    totals.assessmentDues.paid +
    totals.membershipBalance.paid +
    totals.specialFund.paid +
    totals.annualMeeting.paid +
    totals.lateFees.paid
  );
}

/**
 * Sort key for a client-computed column. Returns `null` for values that render
 * blank in the table (missing data, or zero amounts / zero balance) so they are
 * always pushed to the end regardless of sort direction.
 */
function rawSortValue(
  office: Record<string, unknown>,
  field: string,
  billingMap: OfficeBillingMap,
): number | string | null {
  if (field === "firm") {
    const label = officeFirmShortLabel(office);
    return label ? label.toLowerCase() : null;
  }

  const cats = billingMap[String(office.id ?? "")];
  if (!cats) {
    return null;
  }

  if (field === "invoiceDate") {
    if (!cats.invoiceDate) {
      return null;
    }
    const time = Date.parse(cats.invoiceDate);
    return Number.isNaN(time) ? null : time;
  }

  if (field === "paymentStatus") {
    return cats.status ? cats.status.toLowerCase() : null;
  }

  if (field === "balance") {
    const totals = cats.totals;
    if (!totals) {
      return null;
    }
    const balance = sumPaid(totals) - sumInvoiced(totals);
    return balance === 0 ? null : balance;
  }

  const amount = AMOUNT_FIELD_MAP[field];
  if (amount) {
    const value = cats.totals?.[amount.cat]?.[amount.kind];
    const num = Number(value);
    return value && !Number.isNaN(num) && num !== 0 ? num : null;
  }

  return null;
}

/**
 * Returns a copy of the raw office rows sorted by a client-computed billing
 * column. If the active sort is not a client-computed field, the input is
 * returned untouched so server-side ordering is preserved.
 */
export function sortAccountingRawData<T extends Record<string, unknown>>(
  data: T[],
  sorting: AccountingSorting | null | undefined,
  billingMap: OfficeBillingMap,
): T[] {
  if (!Array.isArray(data)) {
    return data;
  }
  if (!sorting || !isAccountingClientSortField(sorting.field)) {
    return data;
  }

  const order = sorting.order === "desc" ? "desc" : "asc";
  const keyed = data.map((office) => ({
    office,
    key: rawSortValue(office, sorting.field, billingMap),
  }));

  keyed.sort((a, b) => {
    if (a.key === null && b.key === null) {
      return 0;
    }
    if (a.key === null) {
      return 1;
    }
    if (b.key === null) {
      return -1;
    }
    let cmp: number;
    if (typeof a.key === "number" && typeof b.key === "number") {
      cmp = a.key - b.key;
    } else {
      cmp = String(a.key).localeCompare(String(b.key));
    }
    return order === "desc" ? -cmp : cmp;
  });

  return keyed.map((entry) => entry.office);
}
